import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bot, Save, Loader, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  getLLMSettings,
  getLLMConfigs,
  updateInterviewLLMSettings,
  updateResumeLLMSettings,
  updateSkillExtractorLLMSettings,
  updateATSScoreLLMSettings
} from '../../services/settingsapi';
import ConfirmationDialog from '../ConfirmationDialog';
import { SettingsSection, SelectField } from '../SettingsUIComponents';

const LLMSettings = ({ canEditSettings, canViewSettings, canDeleteSettings }) => {
  const [llmSettings, setLlmSettings] = useState({
    interview: { provider: '', model: '' },
    analyse_resume: { provider: '', model: '' },
    skill_extractor: { provider: '', model: '' },
    ats_score: { provider: '', model: '' }
  });
  
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmMessage, setLlmMessage] = useState({ type: '', text: '' });
  const [llmConfigs, setLlmConfigs] = useState([]);
  const [llmConfigsLoading, setLlmConfigsLoading] = useState(false);

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Fetch LLM settings on component mount
  useEffect(() => {
    if (!canViewSettings) return;

    const fetchLLMSettings = async () => {
      try {
        setLlmLoading(true);
        const data = await getLLMSettings();
        setLlmSettings({
          interview: { provider: data.interview.provider, model: data.interview.model },
          analyse_resume: { provider: data.analyse_resume.provider, model: data.analyse_resume.model },
          skill_extractor: { provider: data.skill_extractor.provider, model: data.skill_extractor.model },
          ats_score: { provider: data.ats_score.provider, model: data.ats_score.model }
        });
        setLlmMessage({ type: '', text: '' });
      } catch (error) {
        console.error('Error fetching LLM settings:', error);
        setLlmMessage({
          type: 'error',
          text: 'Error loading LLM settings: ' + error.message
        });
      } finally {
        setLlmLoading(false);
      }
    };

    const fetchLLMConfigs = async () => {
      try {
        setLlmConfigsLoading(true);
        const configs = await getLLMConfigs(true);
        setLlmConfigs(configs);
      } catch (error) {
        console.error('Error fetching LLM configs:', error);
      } finally {
        setLlmConfigsLoading(false);
      }
    };

    fetchLLMSettings();
    fetchLLMConfigs();
  }, [canViewSettings]);

  // Get unique providers from configs
  const getUniqueProviders = useCallback(() => {
    const providers = [...new Set(llmConfigs.map(config => config.provider_name || config.provider).filter(Boolean))];
    return providers.map(provider => ({
      value: provider,
      label: provider.charAt(0).toUpperCase() + provider.slice(1)
    }));
  }, [llmConfigs]);

  // Get models for selected provider
  const getModelsForProvider = useCallback((provider) => {
    return llmConfigs
      .filter(config => (config.provider_name || config.provider) === provider)
      .map(config => ({
        value: config.model_name || config.model,
        label: `${config.model_name || config.model} (Temp: ${config.temperature})`
      }))
      .filter(model => Boolean(model.value));
  }, [llmConfigs]);

  // Save LLM section settings
  const saveLLMSection = (section, sectionName, updateFunction, config) => {
    if (!canEditSettings) {
      setLlmMessage({
        type: 'error',
        text: 'You do not have permission to edit settings'
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: `Confirm ${sectionName} Settings`,
      message: `Are you sure you want to save the ${sectionName} LLM configuration? This will be used for ${sectionName.toLowerCase()} tasks.`,
      onConfirm: async () => {
        try {
          setLlmLoading(true);
          await updateFunction(config);
          setLlmMessage({
            type: 'success',
            text: `${sectionName} settings saved successfully!`
          });

          setTimeout(() => setLlmMessage({ type: '', text: '' }), 5000);
        } catch (error) {
          console.error(`Error saving ${sectionName} settings:`, error);
          setLlmMessage({
            type: 'error',
            text: `Error saving ${sectionName} settings: ` + error.message
          });
        } finally {
          setLlmLoading(false);
        }
      }
    });
  };

  const LLMConfigSection = ({ title, icon: Icon, section, config, sectionName, description }) => {
    const handleProviderChange = useCallback((e) => {
      const newProvider = e.target.value;
      const modelsForProvider = getModelsForProvider(newProvider);
      const firstModel = modelsForProvider.length > 0 ? modelsForProvider[0].value : '';
      setLlmSettings(prevSettings => ({
        ...prevSettings,
        [section]: { ...prevSettings[section], provider: newProvider, model: firstModel }
      }));
    }, [section]);

    const handleModelChange = useCallback((e) => {
      setLlmSettings(prevSettings => ({
        ...prevSettings,
        [section]: { ...prevSettings[section], model: e.target.value }
      }));
    }, [section]);

    const updateFunction = useMemo(() => {
      if (section === 'interview') return updateInterviewLLMSettings;
      if (section === 'analyse_resume') return updateResumeLLMSettings;
      if (section === 'skill_extractor') return updateSkillExtractorLLMSettings;
      return updateATSScoreLLMSettings;
    }, [section]);

    const handleSave = useCallback(() => {
      // Only send provider and model to the backend
      const dataToSave = {
        provider: config.provider,
        model: config.model
      };
      saveLLMSection(section, sectionName, updateFunction, dataToSave);
    }, [section, sectionName, updateFunction, config]);

    return (
      <SettingsSection collapsible icon={Icon} title={title} description={description}>
        <div className="space-y-6">
          {llmMessage.text && (
            <div className={`p-4 border rounded-xl flex items-center gap-3 ${
              llmMessage.type === 'success'
                ? 'bg-green-50 border-green-300'
                : 'bg-red-50 border-red-300'
            }`}>
              {llmMessage.type === 'success' ? (
                <CheckCircle className={`w-5 h-5 text-green-600`} />
              ) : (
                <AlertTriangle className={`w-5 h-5 text-red-600`} />
              )}
              <p className={`text-sm font-medium ${llmMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                {llmMessage.text}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SelectField
              label="Provider"
              value={config.provider}
              onChange={handleProviderChange}
              options={getUniqueProviders()}
              canEdit={canEditSettings}
            />

            <SelectField
              label="Model"
              value={config.model}
              onChange={handleModelChange}
              options={getModelsForProvider(config.provider)}
              canEdit={canEditSettings}
            />
          </div>

          <div className="settings-info-banner">
            <p className="text-sm text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--primary)]">Note:</span> All LLM configurations are managed by the backend. API keys are loaded from environment variables, while Temperature, Max Tokens, and Response Time are automatically fetched from the system's LLM configurations. Select a Provider and Model for each service to establish the mapping.
            </p>
          </div>

          <div className="settings-save-row">
            <button
              onClick={handleSave}
              disabled={!canEditSettings || llmLoading}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-md transition-all ${
                canEditSettings && !llmLoading
                  ? 'bg-gradient-to-r from-[var(--primary-hover)] to-[var(--primary-dark)] text-white hover:shadow-lg'
                  : 'bg-[var(--border-color)] text-[var(--text-muted)] cursor-not-allowed opacity-50'
              }`}
              title={!canEditSettings ? 'You do not have permission to edit settings' : ''}
            >
              {llmLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {llmLoading ? 'Saving...' : `Save ${sectionName} Settings`}
            </button>
          </div>
        </div>
      </SettingsSection>
    );
  };

  return (
    <div className="space-y-6">
      <LLMConfigSection
        title="Interview LLM"
        icon={Bot}
        section="interview"
        config={llmSettings.interview}
        sectionName="Interview"
        description="Configure LLM for conducting interviews"
      />

      <LLMConfigSection
        title="Resume Analysis LLM"
        icon={Bot}
        section="analyse_resume"
        config={llmSettings.analyse_resume}
        sectionName="Resume Analysis"
        description="Configure LLM for analyzing resumes"
      />

      <LLMConfigSection
        title="Skill Extraction LLM"
        icon={Bot}
        section="skill_extractor"
        config={llmSettings.skill_extractor}
        sectionName="Skill Extraction"
        description="Configure LLM for extracting skills"
      />

      <LLMConfigSection
        title="ATS Score LLM"
        icon={Bot}
        section="ats_score"
        config={llmSettings.ats_score}
        sectionName="ATS Score"
        description="Configure LLM for ATS scoring"
      />

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Save Changes"
        cancelText="Cancel"
        isDangerous={false}
      />
    </div>
  );
};

export default LLMSettings;
